/**
 * The theme library: user-installed palettes, theme files, and the
 * per-appearance mix ("halves").
 *
 * Forge's adoption of upstream's theme-library machinery, re-expressed over
 * forge's partial-palette model: a theme may omit any role, and an omitted
 * role falls through to the shipped token. The color codec lives in
 * `themeColors.ts`, the file format and stored-record parsing in
 * `themeFiles.ts`; this module owns the installed library, the storage, and
 * the halves. Provenance and adoption rules: docs/port-translation-rules.md.
 *
 * @module
 */
import { type ThemeAppearance, type ThemePalette } from "@cmmd-forge/contracts/theme";

import {
  BUILT_IN_THEME_IDS,
  canonicalizeCustomTheme,
  isRecord,
  parseStoredThemes,
  RESERVED_THEME_IDS,
  toStoredRecord,
} from "./themeFiles";

/** Storage key for installed custom themes. Upstream's, re-prefixed for forge. */
export const CUSTOM_THEMES_STORAGE_KEY = "forge:custom-themes:v1";
/** Storage key for the per-appearance mix. Upstream's, re-prefixed for forge. */

/** A custom theme is a forge palette plus the half it was authored against. */
export interface CustomThemePalette extends ThemePalette {
  readonly appearance: ThemeAppearance;
  readonly collection?: Readonly<{ id: string; label: string }>;
}

/**
 * An automatic-mode mix: a different theme per resolved appearance. Halves
 * only name themes that can render their half; anything else is dropped so a
 * stale mix degrades to the base preference.
 */
export type ThemeHalves = Readonly<{ light?: string; dark?: string }>;

type CustomThemeLibrarySnapshot =
  | Readonly<{
      status: "ready";
      storedThemes: ReadonlyArray<unknown>;
      themes: ReadonlyArray<CustomThemePalette>;
    }>
  | Readonly<{ status: "unavailable"; reason: "malformed" }>
  | Readonly<{ status: "unavailable"; reason: "storage-unavailable"; cause: unknown }>;

let customThemeLibrarySnapshot: CustomThemeLibrarySnapshot | null = null;
const customThemeListeners = new Set<() => void>();

function readCustomThemeLibrarySnapshot(): CustomThemeLibrarySnapshot {
  if (typeof window === "undefined") {
    return { status: "ready", storedThemes: [], themes: [] };
  }

  let raw: string | null;
  try {
    raw = window.localStorage.getItem(CUSTOM_THEMES_STORAGE_KEY);
  } catch (cause) {
    return { status: "unavailable", reason: "storage-unavailable", cause };
  }
  if (!raw) return { status: "ready", storedThemes: [], themes: [] };

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { status: "unavailable", reason: "malformed" };
  }
  if (!Array.isArray(parsed)) return { status: "unavailable", reason: "malformed" };

  return { status: "ready", storedThemes: parsed, themes: parseStoredThemes(parsed) };
}

function getCustomThemeLibrarySnapshot(): CustomThemeLibrarySnapshot {
  if (customThemeLibrarySnapshot === null) {
    customThemeLibrarySnapshot = readCustomThemeLibrarySnapshot();
  }
  return customThemeLibrarySnapshot;
}

function notifyCustomThemeListeners() {
  for (const listener of customThemeListeners) listener();
}

/** Drop the cached snapshot and re-read on the next access. */
export function invalidateCustomThemes() {
  customThemeLibrarySnapshot = null;
  notifyCustomThemeListeners();
}

const EMPTY_CUSTOM_THEMES: ReadonlyArray<CustomThemePalette> = [];

/** Every installed custom theme, in stored order. */
export function getCustomThemes(): ReadonlyArray<CustomThemePalette> {
  const snapshot = getCustomThemeLibrarySnapshot();
  return snapshot.status === "ready" ? snapshot.themes : EMPTY_CUSTOM_THEMES;
}

/** One installed theme by id, built-ins not included. */
export function getCustomTheme(id: string): CustomThemePalette | undefined {
  return getCustomThemes().find((theme) => theme.id === id);
}

/**
 * Subscribe to library changes, including the ones another tab makes: the
 * listener also fires on a storage event for the library key.
 */
export function subscribeToCustomThemes(listener: () => void): () => void {
  customThemeListeners.add(listener);
  if (typeof window === "undefined") {
    return () => customThemeListeners.delete(listener);
  }
  const handleStorage = (event: StorageEvent) => {
    if (event.key === CUSTOM_THEMES_STORAGE_KEY || event.key === null) {
      invalidateCustomThemes();
    }
  };
  window.addEventListener("storage", handleStorage);

  return () => {
    customThemeListeners.delete(listener);
    window.removeEventListener("storage", handleStorage);
  };
}

/**
 * Failed library reads and writes are typed errors, not silent empty arrays:
 * a user whose storage is blocked must see why their theme did not save.
 */
export class ThemeLibraryStorageError extends Error {
  readonly storageKey: string;
  readonly operation: "read" | "write";
  readonly reason: "malformed" | "storage-unavailable";

  constructor(input: {
    storageKey: string;
    operation: "read" | "write";
    reason: "malformed" | "storage-unavailable";
    cause?: unknown;
  }) {
    const direction = input.operation === "read" ? "from" : "to";
    super(`Failed to ${input.operation} the theme library ${direction} ${input.storageKey}.`);
    this.name = "ThemeLibraryStorageError";
    this.storageKey = input.storageKey;
    this.operation = input.operation;
    this.reason = input.reason;
    if (input.cause !== undefined) this.cause = input.cause;
  }
}

function requireWritableLibrary(): Extract<CustomThemeLibrarySnapshot, { status: "ready" }> {
  const snapshot = getCustomThemeLibrarySnapshot();
  if (snapshot.status === "unavailable") {
    throw new ThemeLibraryStorageError({
      storageKey: CUSTOM_THEMES_STORAGE_KEY,
      operation: "read",
      reason: snapshot.reason,
      cause: "cause" in snapshot ? snapshot.cause : undefined,
    });
  }
  return snapshot;
}

function saveCustomThemes(
  storedThemes: ReadonlyArray<unknown>,
  themes: ReadonlyArray<CustomThemePalette>,
): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CUSTOM_THEMES_STORAGE_KEY, JSON.stringify(storedThemes));
    customThemeLibrarySnapshot = { status: "ready", storedThemes, themes };
  } catch (cause) {
    throw new ThemeLibraryStorageError({
      storageKey: CUSTOM_THEMES_STORAGE_KEY,
      operation: "write",
      reason: "storage-unavailable",
      cause,
    });
  }
  notifyCustomThemeListeners();
}

function storedThemeHasId(storedTheme: unknown, themeId: string): boolean {
  return isRecord(storedTheme) && storedTheme.id === themeId;
}

/**
 * Install an imported or created theme. Throws on a reserved or occupied id;
 * callers that mean "replace" handle the collision themselves.
 */
export function installCustomTheme(theme: CustomThemePalette): CustomThemePalette {
  if (RESERVED_THEME_IDS.has(theme.id)) {
    throw new Error(`The theme id "${theme.id}" is reserved.`);
  }
  const library = requireWritableLibrary();
  if (
    BUILT_IN_THEME_IDS.has(theme.id) ||
    library.storedThemes.some((storedTheme) => storedThemeHasId(storedTheme, theme.id))
  ) {
    throw new Error(`A theme named "${theme.name}" is already installed.`);
  }
  const canonicalTheme = canonicalizeCustomTheme(theme);
  const themes = [...library.themes, canonicalTheme];
  saveCustomThemes([...library.storedThemes, toStoredRecord(canonicalTheme)], themes);
  return canonicalTheme;
}

/**
 * Replace one installed theme in place, atomically: the stored records and
 * the parsed cache update together or not at all.
 */
export function updateCustomTheme(theme: CustomThemePalette): CustomThemePalette {
  if (RESERVED_THEME_IDS.has(theme.id)) {
    throw new Error(`The theme id "${theme.id}" is reserved.`);
  }

  const library = requireWritableLibrary();
  const themeIndex = library.themes.findIndex((existing) => existing.id === theme.id);
  if (themeIndex === -1) {
    throw new Error(`The theme "${theme.name}" is not installed.`);
  }

  const canonicalTheme = canonicalizeCustomTheme(theme);
  const nextThemes = [...library.themes];
  nextThemes[themeIndex] = canonicalTheme;

  const nextStoredThemes: unknown[] = [];
  let replaced = false;
  for (const storedTheme of library.storedThemes) {
    if (!storedThemeHasId(storedTheme, theme.id)) {
      nextStoredThemes.push(storedTheme);
    } else if (!replaced) {
      nextStoredThemes.push(toStoredRecord(canonicalTheme));
      replaced = true;
    }
  }
  saveCustomThemes(nextStoredThemes, nextThemes);
  return canonicalTheme;
}

/** Remove one installed theme. Unknown ids are a no-op, not an error. */
export function removeCustomTheme(themeId: string): void {
  removeCustomThemes([themeId]);
}

/** Remove several installed themes in one write, keeping unrelated entries. */
export function removeCustomThemes(themeIds: ReadonlyArray<string>): void {
  const removedIds = new Set(themeIds);
  if (removedIds.size === 0) return;
  const library = requireWritableLibrary();
  const nextThemes = library.themes.filter((theme) => !removedIds.has(theme.id));
  if (nextThemes.length === library.themes.length) return;
  saveCustomThemes(
    library.storedThemes.filter(
      (storedTheme) =>
        !isRecord(storedTheme) ||
        typeof storedTheme.id !== "string" ||
        !removedIds.has(storedTheme.id),
    ),
    nextThemes,
  );
}

// ---------------------------------------------------------------------------

function canResolveThemeHalfOwner(themeId: string): boolean {
  if (BUILT_IN_THEME_IDS.has(themeId)) return true;
  // Forge palettes are dual-half: a custom theme can always render either
  // appearance, because an unset half falls through to the shipped tokens.
  return getCustomTheme(themeId) !== undefined;
}

/**
 * Parse a stored mix, dropping halves that no longer resolve. A theme a user
 * removed must not keep owning an appearance; the base preference takes over.
 */
export function parseThemeHalves(raw: string | null): ThemeHalves | null {
  if (!raw) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (!isRecord(value)) return null;
    const halves: { light?: string; dark?: string } = {};
    for (const appearance of ["light", "dark"] as const) {
      const themeId = value[appearance];
      if (typeof themeId !== "string") continue;
      if (canResolveThemeHalfOwner(themeId)) {
        halves[appearance] = themeId;
      }
    }
    return halves.light !== undefined || halves.dark !== undefined ? halves : null;
  } catch {
    return null;
  }
}

/** The theme that should render the given appearance under a mix, if any. */
export function resolveThemeHalf(
  theme: string,
  halves: ThemeHalves | null,
  appearance: ThemeAppearance,
): string {
  return halves?.[appearance] ?? theme;
}
